import { Box, Divider, FormField, Input, Text } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useState } from 'react';
import {
  EditorContext,
  ModalContext,
  RicosContext,
  usePluginExportsContext,
  ZIndexContext,
} from 'ricos-context';
import { type ImageData, PluginContainerData_Alignment } from 'ricos-schema';
import type { Media } from 'ricos-schema/next';
import {
  FooterActions,
  ImageNew as Image,
  SidePanelLayout,
  ToggleButton,
} from 'wix-rich-content-ui-components';

import { imageModals } from '../consts';
import {
  SETTINGS_ALT_INPUT_TEXT,
  SETTINGS_CAPTION_INPUT,
  SETTINGS_DISABLE_DOWNLOAD_TOGGLE,
  SETTINGS_DISABLE_EXPAND_TOGGLE,
  SETTINGS_IMAGE_PREVIEW,
  SETTINGS_SPOILER_TOGGLE,
} from '../dataHooks';
import type { ImagePluginEditorConfig } from '../types';
import { IMAGE_TYPE } from '../types';
import { useSpoilerPlugin } from './use-spoiler-plugin';

interface SettingsProps {
  nodeId: string;
  config: ImagePluginEditorConfig;
}

type SettingsState = {
  image: ImageData['image'];
  caption: string;
  altText: string;
  disableExpand: boolean;
  disableDownload: boolean;
  isMarkedAsSpoiler: boolean | undefined;
};

export const Settings: FC<SettingsProps> = ({ nodeId, config }) => {
  const { t, isMobile } = useContext(RicosContext);
  const modalService = useContext(ModalContext) || {};
  const { getEditorCommands } = useContext(EditorContext);
  const zIndexService = useContext(ZIndexContext);
  const exports = usePluginExportsContext();

  const [initialData] = useState<ImageData>(() =>
    getEditorCommands().getBlockComponentData(nodeId)
  );
  const [state, setState] = useState<SettingsState>(() =>
    fromComponentData({ componentData: initialData, config })
  );

  const isSpoilerEnabled = useSpoilerPlugin();

  const closeModal = () => {
    modalService.closeModal(imageModals.settings);
  };

  const handleOnChange = (value: Partial<SettingsState>) => {
    getEditorCommands().setBlock(
      nodeId,
      IMAGE_TYPE,
      toComponentData({
        componentData: initialData,
        config,
        formValue: { ...state, ...value },
      })
    );
    setState(prev => ({ ...prev, ...value }));
  };

  const requiredHeight = isMobile ? 157 : 240;
  const requiredWidth = requiredHeight;

  const AltTextComponent = exports?.aiIntegrations?.image?.altTextInput;
  const hasCaptionTriggerExport = !!exports?.aiIntegrations?.image?.captionTrigger;

  return (
    <SidePanelLayout
      onCloseButtonClick={closeModal}
      header={{ title: t('ImageSettings_Header') }}
      content={{
        children: (
          <Box direction="vertical" height={'100%'} gap="SP4">
            <Image
              dataHook={SETTINGS_IMAGE_PREVIEW}
              alt={state.altText || t('ImagePlugin_Image_Preview_Alt')}
              src={state.image?.src?.id || state.image?.src?.url || ''}
              width={Number(state.image?.width)}
              height={Number(state.image?.height)}
              requiredHeight={requiredHeight}
              requiredWidth={requiredWidth}
            />
            {!hasCaptionTriggerExport && (
              <FormField label={t('ImageSettings_Caption_Label')}>
                <Input
                  dataHook={SETTINGS_CAPTION_INPUT}
                  placeholder={t('ImageSettings_Caption_Input_Placeholder')}
                  value={state.caption}
                  onChange={e => handleOnChange({ caption: e.target.value })}
                />
              </FormField>
            )}
            {AltTextComponent ? (
              <AltTextComponent
                image={state.image as Media}
                altText={state.altText}
                onChange={value => handleOnChange({ altText: value })}
              />
            ) : (
              <FormField
                label={t('ImageSettings_Alt_Label')}
                infoContent={
                  <Box direction="vertical" gap="SP1">
                    <Text light size="small">
                      {t('ImageSettings_Alt_Label_Tooltip')}
                    </Text>
                  </Box>
                }
                infoTooltipProps={{
                  appendTo: el => Boolean(el.getAttribute('data-hook')?.includes('ricos-portal')),
                  zIndex: zIndexService.getZIndex('TOOLTIP'),
                }}
              >
                <Input
                  dataHook={SETTINGS_ALT_INPUT_TEXT}
                  type="text"
                  placeholder={t('ImageSettings_Alt_Input_Placeholder')}
                  value={state.altText}
                  onChange={e => handleOnChange({ altText: e.target.value })}
                />
              </FormField>
            )}
            <ToggleButton
              title={t('ImagePlugin_Settings_ImageOpensInExpandMode_Label')}
              checked={!state.disableExpand}
              onChange={() => handleOnChange({ disableExpand: !state.disableExpand })}
              infoContent={t('ImageSettings_Expand_Mode_Toggle')}
              dataHook={SETTINGS_DISABLE_EXPAND_TOGGLE}
            />
            <ToggleButton
              title={t('ImagePlugin_Settings_ImageCanBeDownloaded_Label')}
              checked={!state.disableDownload}
              onChange={() => handleOnChange({ disableDownload: !state.disableDownload })}
              infoContent={t('ImagePlugin_Settings_ImageCanBeDownloaded_Tooltip')}
              dataHook={SETTINGS_DISABLE_DOWNLOAD_TOGGLE}
            />
            {isSpoilerEnabled ? (
              <>
                <Divider />
                <ToggleButton
                  title={t('ImageSettings_Spoiler_Toggle')}
                  checked={Boolean(state.isMarkedAsSpoiler)}
                  onChange={() => handleOnChange({ isMarkedAsSpoiler: !state.isMarkedAsSpoiler })}
                  infoContent={t('Spoiler_Toggle_Tooltip')}
                  dataHook={SETTINGS_SPOILER_TOGGLE}
                />
              </>
            ) : null}
          </Box>
        ),
      }}
      footer={{
        children: (
          <FooterActions
            onSave={closeModal}
            onCancel={() => {
              getEditorCommands().setBlock(nodeId, IMAGE_TYPE, initialData);
              closeModal();
            }}
          />
        ),
      }}
    />
  );
};

const fromComponentData = ({
  componentData,
  config,
}: {
  componentData: ImageData;
  config: ImagePluginEditorConfig;
}): SettingsState => {
  return {
    image: componentData.image,
    caption: componentData.caption ?? '',
    altText: componentData.altText ?? '',
    disableExpand: componentData.disableExpand ?? Boolean(config.disableExpand),
    disableDownload: componentData.disableDownload ?? Boolean(config.disableDownload),
    isMarkedAsSpoiler: componentData.containerData?.spoiler?.enabled ?? undefined,
  };
};

const toComponentData = ({
  componentData,
  config,
  formValue,
}: {
  componentData: ImageData | undefined;
  config: ImagePluginEditorConfig;
  formValue: SettingsState;
}): ImageData => {
  const { disableExpand, disableDownload, caption, altText, image, isMarkedAsSpoiler } = formValue;

  const getValueOrDefault = (setting: boolean | undefined, value: boolean) => {
    if (setting !== undefined) {
      return setting === value ? undefined : value;
    }

    return value === false ? undefined : value;
  };

  return {
    ...componentData,
    image,
    caption: caption || undefined,
    altText: altText || undefined,
    disableExpand: getValueOrDefault(config.disableExpand, disableExpand),
    disableDownload: getValueOrDefault(config.disableDownload, disableDownload),
    containerData: {
      alignment: componentData?.containerData?.alignment ?? PluginContainerData_Alignment.LEFT,
      ...componentData?.containerData,
      spoiler: (() => {
        if (!isMarkedAsSpoiler) {
          return undefined;
        }

        return {
          ...componentData?.containerData?.spoiler,
          enabled: isMarkedAsSpoiler,
        };
      })(),
    },
  };
};
